import { useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/hooks/use-auth'
import {
  FEATURE_FLAGS_REFRESH_MS,
  FeatureNames,
  fetchAccountFeatures,
  featureFlagsQueryKey,
  isFeatureEnabledIn,
} from '@/lib/featureFlags'

export type UseFeatureFlagsResult = {
  isFeatureEnabled: (feature: FeatureNames) => boolean
  /** Faux tant que la première lecture n'a pas répondu : ne pas rediriger avant. */
  isLoaded: boolean
}

/**
 * Flags visibles pour l'utilisateur connecté (TEC-1701), relus toutes les 15 s quand l'onglet est
 * visible. Une clé par utilisateur : une impersonation relit les flags de l'utilisateur usurpé.
 */
export const useFeatureFlags = (): UseFeatureFlagsResult => {
  const { isAuthenticated, user } = useAuth()
  const { data, isSuccess, isError } = useQuery({
    queryKey: featureFlagsQueryKey(user?.uid),
    queryFn: fetchAccountFeatures,
    enabled: isAuthenticated,
    staleTime: 0,
    refetchInterval: FEATURE_FLAGS_REFRESH_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  })

  const isFeatureEnabled = useCallback(
    (feature: FeatureNames) => isFeatureEnabledIn(data, feature),
    [data]
  )

  return { isFeatureEnabled, isLoaded: !isAuthenticated || isSuccess || isError }
}

export const useFeatureFlag = (feature: FeatureNames): boolean =>
  useFeatureFlags().isFeatureEnabled(feature)
